import { createSelector } from "@reduxjs/toolkit";
import React, { Dispatch } from "react";
import { connect } from "react-redux";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { ParkingProductType } from "../../models";
import { DelegeeWithProductsModel } from "../../models/delegations/DelegatedParkingProductsModel";
import { InviteDelegeeRequestModel, Product } from "../../models/InvitePedestrianRightRequestModel";
import { ContractType } from "../../models/ParkingProductModel";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { useAppDispatch, useAppSelector } from "../../store";
import * as entryRightsActions from "../../store/actions/entryRights.actions";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import * as parkingProductsThunks from "../../store/actions/parkingProducts.thunks";
import { RootReducer } from "../../store/reducers";
import DelegationForm from "../DelegationForm/DelegationForm";
import { DelegationFormDetails, DelegeeWithPmcs } from "../DelegationForm/DelegationFormDetails";

interface IDelegeeProps extends DispatchProps {
    onClose: () => void;
    refreshHandler: () => void;
}

function mapToDelegeeWithPmcs(delegee: DelegeeWithProductsModel): DelegeeWithPmcs {
    return {
        id: delegee.registrationId,
        name: delegee.name,
        emailAddress: delegee.emailAddress,
        products: delegee.products.map(p => ({pmcId: p.pmcId, contractType: p.contractType}))
    };
}

const selectParkingParkingProducts = createSelector(
    [
        (state: RootReducer) => state.parkingProducts.parkingProducts,
        (_, isEditMode) => isEditMode,
    ],
    (parkingProducts, isEditMode) => {
        return {
            ...parkingProducts,
            data: isEditMode
                ? parkingProducts.data
                : parkingProducts.data?.filter((pp) => pp.productType === ParkingProductType.ParkingRights),
        };
    }
);

const selectDelegeeDetails = createSelector(
    (state: RootReducer) => state.parkingProducts.delegeeDetails,
    (delegeeWithProducts) => ({
        ...delegeeWithProducts,
        data: delegeeWithProducts.data && mapToDelegeeWithPmcs(delegeeWithProducts.data),
    })
);

const selectFormDetails = createSelector(
    [
        selectParkingParkingProducts,
        selectDelegeeDetails,
        (s: RootReducer) => {
            const { loading, error } = s.parkingProducts;
            // Block when either the parking rights or the entry rights bulk import is still running.
            const isBulkImportCompleted = s.parkingProducts.isBulkImportCompleted && s.entryRights.isBulkImportCompleted;
            return { loading, isBulkImportCompleted, error };
        },
    ],
    (parkingProducts, delegeeDetails, rest): DelegationFormDetails => {
        return ({
        parkingProducts,
        delegeeDetails,
        ...rest,
    });
}
);

const ParkingRightsForm: React.FC<IDelegeeProps> = (props) => {
    const {
        getParkingProducts,
        clearDelegeeDetails,
        resetBulkImportProgress,
        getBulkImportStatus,
        disposeError,
    } = props;

    const { fleetManagerState } = useFleetManagerContext();
    const formDetails = useAppSelector(state => selectFormDetails(state, fleetManagerState.isEditDelegeeMode));
    const dispatch = useAppDispatch();

    const inviteDelegee = async (parkingRights: ParkingRightsRequestModel, callback?: () => void) => {
        if (parkingRights.products.some(p => p.contractType !== ContractType.SeasonTicket)) {
            const request = createInviteModel(parkingRights, parkingRights.products);
            const inviteResult = await dispatch(entryRightsActions.inviteActionThunk(request)).unwrap();
            if (inviteResult.ok) {
                callback?.();
            }
        } else {
            const delegateResult = await dispatch(parkingProductsThunks.delegateParkingRightsThunk(parkingRights)).unwrap();
            if (delegateResult.ok) {
                callback?.();
            }
        }
    };

    const updateDelegee = async (parkingRights: ParkingRightsRequestModel, callback?: () => void) => {
        const updateResult = await dispatch(parkingProductsThunks.updateDelegationThunk(parkingRights)).unwrap();
        if (updateResult.ok) {
            callback?.();
        }
    };

    const isEditMode = fleetManagerState.isEditDelegeeMode;
    return (
        <div>
            <DelegationForm
                onClose={props.onClose}
                refreshHandler={props.refreshHandler}
                getProducts={getParkingProducts}
                inviteDelegee={inviteDelegee}
                updateDelegation={updateDelegee}
                getDelegeeDetails={(registrationId, language, seasonTicketOwnerCrmId) =>
                    dispatch(parkingProductsThunks.getDelegeeDetailsThunk({ registrationId, language, seasonTicketOwnerCrmId }))
                }
                clearDelegeeDetails={clearDelegeeDetails}
                resetBulkImportProgress={resetBulkImportProgress}
                getBulkImportStatus={getBulkImportStatus}
                disposeError={disposeError}
                isEditMode={isEditMode}
                translationContextName="parkingRightsForm"
                delegationFormDetails={formDetails}
            />
        </div>
    );
};

function createInviteModel(
    parkingRights: ParkingRightsRequestModel,
    products: Product[]
): InviteDelegeeRequestModel {
    return {
        customerId: parkingRights.seasonTicketOwnerCrmId,
        name: parkingRights.name,
        email: parkingRights.email,
        products: products
    };
}


const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    getParkingProducts: (seasonTicketOwnerCrmId: string, language: string) =>
        dispatch(
            parkingProductsThunks.getParkingProducts({ seasonTicketOwnerCrmId, location: language })
        ),
    disposeError: () => dispatch(parkingProductsActions.disposeError()),
    clearDelegeeDetails: () => dispatch(parkingProductsActions.clearDelegeeDetails()),
    resetBulkImportProgress: () => dispatch(parkingProductsActions.resetBulkImportProgress()),
    getBulkImportStatus: (seasonTicketOwnerCrmId: string): Promise<unknown> =>
        Promise.all([
            dispatch(parkingProductsActions.getBulkImportStatus(seasonTicketOwnerCrmId)),
            dispatch(entryRightsActions.getBulkImportStatusActions(seasonTicketOwnerCrmId)),
        ]),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(ParkingRightsForm);
